import { useMemo } from 'react'
import useLoyaltyPointsEarnCalculator from './useLoyaltyPointsEarnCalculator'

export interface GroupedLoyaltyEarnCalculationRequest<TGroupKey extends string | number> {
  groupKey: TGroupKey
  request: App.LoyaltyPointsEarnCalculationRequest
}

export interface LoyaltyEarnTotals {
  points: number
  statusCredits: number
}

const EMPTY_LOYALTY_EARN_TOTALS: Array<LoyaltyEarnTotals | undefined> = []

export function buildGroupedLoyaltyEarnTotals<TGroupKey extends string | number>(
  groupKeys: Array<TGroupKey>,
  calculationRequests: Array<GroupedLoyaltyEarnCalculationRequest<TGroupKey>>,
  pointsCalculations: Array<App.StatefulData<App.LoyaltyPointsEarnCalculation> | undefined>,
): Array<LoyaltyEarnTotals | undefined> {
  if (groupKeys.length === 0) {
    return EMPTY_LOYALTY_EARN_TOTALS
  }

  const totalsByGroupKey = new Map<TGroupKey, LoyaltyEarnTotals>()
  calculationRequests.forEach((calculationRequest, calculationIndex) => {
    const calculation = pointsCalculations[calculationIndex]
    if (!calculation?.data) {
      return
    }

    const existingTotals = totalsByGroupKey.get(calculationRequest.groupKey) ?? {
      points: 0,
      statusCredits: 0,
    }
    totalsByGroupKey.set(calculationRequest.groupKey, {
      points: existingTotals.points + calculation.data.points,
      statusCredits: existingTotals.statusCredits + calculation.data.statusCredits + calculation.data.bonusStatusCredits,
    })
  })

  return groupKeys.map((groupKey) => {
    const totals = totalsByGroupKey.get(groupKey)
    return totals && (totals.points > 0 || totals.statusCredits > 0) ? totals : undefined
  })
}

export default function useGroupedLoyaltyEarnTotals<TGroupKey extends string | number>(
  groupKeys: Array<TGroupKey>,
  calculationRequests: Array<GroupedLoyaltyEarnCalculationRequest<TGroupKey>>,
): Array<LoyaltyEarnTotals | undefined> {
  const requestsOnly = useMemo(() => calculationRequests.map(requestInfo => requestInfo.request), [calculationRequests])
  const { pointsCalculations } = useLoyaltyPointsEarnCalculator(requestsOnly, {
    skipAll: requestsOnly.length === 0,
    useBatch: true,
  })

  return useMemo(() => buildGroupedLoyaltyEarnTotals(
    groupKeys,
    calculationRequests,
    pointsCalculations,
  ), [groupKeys, calculationRequests, pointsCalculations])
}
